import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';

import { Button } from '@/design-system/button';
import { TableData, type TableDataColumn } from '@/design-system/table-data';
import { toast } from '@/design-system/toaster';

import { parseWarehouseError, warehouseApi } from '../../api/warehouse.api';
import { ActionDialog } from '../../components/dialogs';
import EditablePickingTable from '../../components/EditablePickingTable';
import { FormField, TextInput } from '../../components/form';
import type { OrderedSku } from '../../components/pickingEdit';
import ReadyToPickupModal from '../../components/ReadyToPickupModal';
import {
  codeCls,
  DetailHeader,
  DetailSkeleton,
  KV,
  LoadError,
  linkCls,
  metaCls,
  nameCls,
  numCls,
  Section,
  StatusBadge,
} from '../../components/ui';
import { fmtDate, fmtDateTime, fmtQty, isExpired } from '../../format';
import { useLeaveGuard } from '../../hooks/useLeaveGuard';
import {
  goodsIssueHistory,
  HistorySection,
  outboundOrderHistory,
} from '../../components/history';
import { useLoad } from '../../hooks/useLoad';
import { fieldErrorsOf } from '../../hooks/useServerErrors';
import { sortByBin } from '../../print/PickingListPdf';
import WarehousePrintModal, {
  type PrintTarget,
} from '../../print/WarehousePrintModal';
import { GOODS_ISSUE_STATUS, isPickingStage } from '../../status';
import {
  type GoodsIssueDetail,
  type GoodsIssueLine,
  type UpdateGoodsIssueLinesPayload,
} from '../../types';
import { useWarehouse } from '../../WarehouseContext';
import { WAREHOUSE_PATHS } from '../../paths';

const ORDERED_COLUMNS: TableDataColumn<OrderedSku>[] = [
  {
    key: 'sku',
    title: 'SKU',
    render: (_v, l) => <span className={codeCls}>{l.sku}</span>,
  },
  {
    key: 'itemName',
    title: 'Product name',
    render: (_v, l) => <span className={nameCls}>{l.itemName}</span>,
  },
  {
    key: 'qty',
    title: 'Qty order',
    align: 'right',
    render: (_v, l) => <span className={numCls}>{fmtQty(l.qty)}</span>,
  },
  {
    key: 'uomName',
    title: 'UoM',
    render: (_v, l) => <span className={metaCls}>{l.uomName}</span>,
  },
];

/**
 * Picked items, read-only. Lines are picked as allocated when the picking
 * order is created (qty picked = qty reserved); changes go through Edit.
 */
const PICKED_COLUMNS: TableDataColumn<GoodsIssueLine>[] = [
  {
    key: 'sku',
    title: 'SKU',
    render: (_v, l) => (
      <div>
        <p className={codeCls}>{l.sku}</p>
        <p className={metaCls}>{l.itemName}</p>
      </div>
    ),
  },
  {
    key: 'loc',
    title: 'Item location',
    render: (_v, l) => (
      <span className={nameCls}>{l.storagePath || l.storageName}</span>
    ),
  },
  {
    key: 'batchNumber',
    title: 'Batch number',
    render: (_v, l) => (
      <div>
        <p className={numCls}>{l.batchNumber}</p>
        {l.expiredDate && (
          <p
            className={`${metaCls} ${isExpired(l.expiredDate) ? 'text-(--state-error-base)' : ''}`}
          >
            Exp {fmtDate(l.expiredDate)}
            {isExpired(l.expiredDate) ? ' · expired' : ''}
          </p>
        )}
      </div>
    ),
  },
  {
    key: 'qtyPicked',
    title: 'Qty picked',
    align: 'right',
    render: (_v, l) => <span className={numCls}>{fmtQty(l.qtyReserved)}</span>,
  },
  {
    key: 'uomName',
    title: 'UoM',
    render: (_v, l) => <span className={metaCls}>{l.uomName}</span>,
  },
];

type BusyKind = 'picker' | 'ready' | 'delete' | 'edit';

const PickingDetailPage: React.FC = () => {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { refreshToken } = useWarehouse();
  const detail = useLoad(
    (signal) => warehouseApi.getGoodsIssue(id, { signal }),
    [id, refreshToken]
  );
  // The order's event log is the full history (picking, ready, shipping,
  // carrier / manual status) — one extra read once the GI is known.
  const orderID = detail.data?.outboundOrder?.id ?? null;
  const history = useLoad(
    (signal) => warehouseApi.getOutboundOrder(orderID as string, { signal }),
    [orderID, refreshToken, detail.data?.goodsIssue.status],
    !!orderID
  );
  /** Picker name being typed; null = showing the saved one. */
  const [pickerDraft, setPickerDraft] = useState<string | null>(null);
  const [pickerError, setPickerError] = useState<string | null>(null);
  const [busy, setBusy] = useState<BusyKind | null>(null);
  const busyRef = useRef(false);
  /** "Edit picked items" mode (the reservation list as editable rows). */
  const [editing, setEditing] = useState(false);
  /** Edit mode holds changes that are not saved yet. */
  const [editDirty, setEditDirty] = useState(false);
  /** Where an in-app link wanted to go while edits were unsaved. */
  const [leaveTo, setLeaveTo] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [readyOpen, setReadyOpen] = useState(false);
  /** Picker validation from the server, shown in the ready dialog. */
  const [readyError, setReadyError] = useState<string | null>(null);
  const [print, setPrint] = useState<PrintTarget | null>(null);

  const gi = detail.data?.goodsIssue;
  const picker = pickerDraft ?? gi?.pickerName ?? '';
  useEffect(() => {
    setPickerDraft(null);
    setPickerError(null);
  }, [gi?.pickerName]);

  const lines = useMemo(
    () => sortByBin(detail.data?.lines ?? []),
    [detail.data]
  );
  const ordered = useMemo(() => {
    const by = new Map<string, OrderedSku>();
    lines.forEach((l) => {
      const cur = by.get(l.sku);
      // 2-decimal qtys: keep the sum free of float noise.
      if (cur) cur.qty = Math.round((cur.qty + l.qtyReserved) * 100) / 100;
      else
        by.set(l.sku, {
          sku: l.sku,
          itemName: l.itemName,
          uomName: l.uomName,
          qty: l.qtyReserved,
        });
    });
    return Array.from(by.values());
  }, [lines]);

  const leaveEdit = useCallback(() => {
    setEditing(false);
    setEditDirty(false);
  }, []);
  useLeaveGuard(
    editing && editDirty && !!gi && isPickingStage(gi.status),
    setLeaveTo
  );

  if (detail.loading && !detail.data) return <DetailSkeleton />;
  if (detail.error || !detail.data || !gi) {
    return (
      <div className="space-y-5 p-6">
        <DetailHeader
          backTo={WAREHOUSE_PATHS.picking}
          backLabel="Picking"
          title={id}
        />
        <LoadError
          message={detail.error?.message ?? 'Picking tidak ditemukan'}
          onRetry={detail.reload}
        />
      </div>
    );
  }
  if (gi.status === 'READY_TO_PICKUP' || gi.status === 'SHIPPED') {
    return <Navigate to={`${WAREHOUSE_PATHS.shipping}/${gi.id}`} replace />;
  }

  const oo = detail.data.outboundOrder;
  const editable = isPickingStage(gi.status);
  // Like the picker, the destination city must be settled before Ready to
  // pick up (the server refuses with 422): the dialog asks for it when the
  // order has none; a TRANSFER ships to a hub.
  const cityMissing =
    !!oo && oo.type !== 'TRANSFER' && oo.destinationCityID === null;
  const editingNow = editing && editable;

  const apply = (next: GoodsIssueDetail | undefined) => {
    if (next) detail.setData(next);
    else detail.reload();
  };

  /** One action at a time; a failure is a toast. */
  const run = async (kind: BusyKind, fn: () => Promise<void>) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(kind);
    try {
      await fn();
    } catch (err) {
      toast.error(parseWarehouseError(err).message);
    } finally {
      busyRef.current = false;
      setBusy(null);
    }
  };

  const pickerDirty = picker.trim() !== (gi.pickerName ?? '');

  const savePicker = () => {
    const name = picker.trim();
    if (!name) {
      setPickerError('Nama picker wajib diisi');
      return;
    }
    setPickerError(null);
    return run('picker', async () => {
      try {
        const res = await warehouseApi.setPicker(gi.id, name);
        apply(res?.detail);
        toast.info(`Picker: ${name}`);
      } catch (err) {
        const e = parseWarehouseError(err);
        if (e.status !== 422) throw err;
        setPickerError(e.fieldErrors[0]?.message ?? e.message);
      }
    });
  };

  /**
   * Edit → Simpan: replaces the reservation list. Rejects with the server
   * error so the editor can put a 422 on its rows.
   */
  const saveLines = async (body: UpdateGoodsIssueLinesPayload) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy('edit');
    try {
      const res = await warehouseApi.updateGoodsIssueLines(gi.id, body);
      apply(res?.detail);
      leaveEdit();
      toast.success('Picking list diperbarui');
    } finally {
      busyRef.current = false;
      setBusy(null);
    }
  };

  /**
   * Ready to pick up with the picker from the dialog (set in the same
   * request). A picker validation error stays in the dialog; anything else
   * is a toast and a reload.
   */
  const markReady = async (name: string, cityID: number | null) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy('ready');
    setReadyError(null);
    try {
      const res = await warehouseApi.readyToPickup(
        gi.id,
        name,
        undefined,
        cityID ?? undefined
      );
      const sj = res?.detail?.goodsIssue.suratJalanNo;
      toast.success(
        'Ready to pick up',
        sj ? { description: `Surat jalan ${sj}` } : undefined
      );
      navigate(`${WAREHOUSE_PATHS.shipping}/${gi.id}`);
    } catch (err) {
      const e = parseWarehouseError(err);
      const { list, message } = fieldErrorsOf(err);
      const onPicker = list.find((x) => /pickerName$/.test(x.field));
      if (onPicker || (e.status === 422 && e.code === 'VALIDATION')) {
        setReadyError(onPicker?.message ?? message);
      } else {
        toast.error(message);
        setReadyOpen(false);
        detail.reload();
      }
    } finally {
      busyRef.current = false;
      setBusy(null);
    }
  };

  return (
    <div className="space-y-5 p-6">
      <DetailHeader
        backTo={WAREHOUSE_PATHS.picking}
        backLabel="Picking"
        meta={
          <>
            Dibuat {fmtDateTime(gi.createdAt)} · oleh{' '}
            <span className="font-semibold text-text-sub-600">
              {gi.createdBy}
            </span>
          </>
        }
        title={gi.code}
        badges={<StatusBadge meta={GOODS_ISSUE_STATUS[gi.status]} />}
        actions={
          // While picked items are being edited only the editor acts.
          editingNow ? undefined : (
            <>
              <Button
                tone="neutral"
                style="stroke"
                onClick={() => setPrint({ kind: 'picking', id: gi.id })}
                leftIcon={<i className="ri-printer-line text-[14px]" />}
              >
                Print picking list
              </Button>
              {editable && (
                <>
                  <Button
                    tone="destructive"
                    style="ghost"
                    disabled={busy !== null}
                    onClick={() => setConfirmDelete(true)}
                    leftIcon={<i className="ri-delete-bin-line text-[14px]" />}
                  >
                    Delete
                  </Button>
                  <Button
                    data-testid="picking-ready"
                    disabled={busy !== null}
                    onClick={() => {
                      setReadyError(null);
                      setReadyOpen(true);
                    }}
                    leftIcon={<i className="ri-check-line text-[14px]" />}
                  >
                    Ready to pick up
                  </Button>
                </>
              )}
            </>
          )
        }
      />

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
        <div className="flex flex-col gap-5 xl:col-span-2">
          <Section title="Ordered item">
            <TableData size="sm" data={ordered} columns={ORDERED_COLUMNS} />
          </Section>
          <Section
            title="Picked item"
            right={
              editable && !editingNow ? (
                <Button
                  tone="neutral"
                  style="stroke"
                  size="sm"
                  data-testid="picking-edit-open"
                  disabled={busy !== null}
                  onClick={() => {
                    setEditDirty(false);
                    setEditing(true);
                  }}
                  leftIcon={<i className="ri-pencil-line text-[14px]" />}
                >
                  Edit
                </Button>
              ) : undefined
            }
          >
            {editingNow ? (
              <EditablePickingTable
                goodsIssueID={gi.id}
                lines={lines}
                ordered={ordered}
                saving={busy === 'edit'}
                onSave={saveLines}
                onCancel={leaveEdit}
                onStale={() => {
                  leaveEdit();
                  detail.reload();
                }}
                onDirtyChange={setEditDirty}
                confirmsPick={gi.status === 'RESERVED'}
              />
            ) : (
              <div data-testid="picking-picked-table">
                <TableData size="sm" data={lines} columns={PICKED_COLUMNS} />
              </div>
            )}
          </Section>
        </div>
        <div className="flex flex-col gap-5">
          <Section title="Picker">
            {editable ? (
              <FormField label="Picker" htmlFor="gi-picker" error={pickerError}>
                <div className="flex items-center gap-2">
                  <TextInput
                    id="gi-picker"
                    size="md"
                    value={picker}
                    onChange={(v) => {
                      setPickerDraft(v);
                      setPickerError(null);
                    }}
                    invalid={!!pickerError}
                    placeholder="Nama picker"
                    rootClassName="flex-1"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && pickerDirty && !editingNow) {
                        void savePicker();
                      }
                    }}
                  />
                  {pickerDirty && !editingNow && (
                    <Button
                      tone="neutral"
                      style="stroke"
                      data-testid="picking-picker-save"
                      loading={busy === 'picker'}
                      disabled={busy !== null && busy !== 'picker'}
                      onClick={() => void savePicker()}
                    >
                      Simpan
                    </Button>
                  )}
                </div>
              </FormField>
            ) : (
              <KV label="Picker" value={gi.pickerName} />
            )}
          </Section>
          <Section title="Info">
            <KV
              label="Request ID"
              value={
                oo ? (
                  <Link
                    to={`${WAREHOUSE_PATHS.outboundRequest}/${oo.id}`}
                    className={linkCls}
                  >
                    {oo.code}
                  </Link>
                ) : (
                  gi.reference
                )
              }
            />
            <KV
              label="External ID"
              value={
                oo ? <span className={codeCls}>{oo.externalID}</span> : null
              }
            />
            <KV
              label="Client"
              value={gi.client?.name ?? `Client #${gi.clientID}`}
            />
            <KV label="Hub" value={gi.hub?.name ?? `Hub #${gi.hubID}`} />
            <KV label="Customer" value={oo?.recipientName} />
            <KV
              label="Expected delivery"
              value={oo ? fmtDate(oo.expectedDeliveryDate) : null}
            />
          </Section>
          <HistorySection
            entries={
              oo
                ? outboundOrderHistory(history.data?.events ?? [])
                : goodsIssueHistory(gi)
            }
            loading={history.loading}
          />
        </div>
      </div>

      <ReadyToPickupModal
        open={readyOpen && editable}
        code={gi.code}
        initialPicker={gi.pickerName}
        askCity={cityMissing}
        busy={busy === 'ready'}
        error={readyError}
        onClose={() => setReadyOpen(false)}
        onConfirm={(name, cityID) => void markReady(name, cityID)}
      />
      <ActionDialog
        open={confirmDelete}
        title="Delete picking"
        subtitle={gi.code}
        description="Reserve stok dilepas dan outbound request kembali Open."
        confirmLabel="Delete picking"
        tone="destructive"
        testId="picking-delete-dialog"
        busy={busy === 'delete'}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() =>
          run('delete', async () => {
            await warehouseApi.cancelGoodsIssue(gi.id);
            toast.info('Picking dihapus', { description: gi.code });
            setConfirmDelete(false);
            navigate(
              oo
                ? `${WAREHOUSE_PATHS.outboundRequest}/${oo.id}`
                : WAREHOUSE_PATHS.picking
            );
          })
        }
      />
      <ActionDialog
        open={leaveTo !== null}
        title="Tinggalkan halaman"
        subtitle={gi.code}
        description="Perubahan picking list yang belum disimpan akan hilang."
        confirmLabel="Tinggalkan"
        tone="destructive"
        testId="picking-leave-dialog"
        onClose={() => setLeaveTo(null)}
        onConfirm={() => {
          const to = leaveTo;
          setLeaveTo(null);
          leaveEdit();
          if (to) navigate(to);
        }}
      />
      <WarehousePrintModal target={print} onClose={() => setPrint(null)} />
    </div>
  );
};

export default PickingDetailPage;
